import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RutField, fillEmpty } from '../components/RutField';
import { toast } from 'sonner';
import {
  PenTool,
  ArrowLeft,
  FileText,
  User,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Plus,
  Trash2,
  Loader2,
  Send,
  Lock,
  Fingerprint,
  Smartphone,
  KeyRound,
  Download,
  Eye,
  ChevronRight,
  Info,
  Sparkles
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { color: 'amber', label: 'Pendiente', icon: Clock },
  signed: { color: 'green', label: 'Firmado', icon: CheckCircle },
  rejected: { color: 'red', label: 'Rechazado', icon: XCircle },
  expired: { color: 'slate', label: 'Expirado', icon: AlertCircle },
  cancelled: { color: 'slate', label: 'Cancelado', icon: XCircle }
};

const ElectronicSignaturePage = () => {
  const { api } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('create'); // create, processes, info, firmaweb
  const [providers, setProviders] = useState([]);
  const [signatureTypes, setSignatureTypes] = useState([]);
  const [verificationMethods, setVerificationMethods] = useState([]);
  const [myProcesses, setMyProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [legalInfo, setLegalInfo] = useState(null);
  const [firmawebInfo, setFirmawebInfo] = useState(null);
  
  // Form state
  const [documentName, setDocumentName] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('firmaweb');
  const [selectedType, setSelectedType] = useState('advanced');
  const [selectedMethod, setSelectedMethod] = useState('otp_email');
  const [signers, setSigners] = useState([{ rut: '', name: '', email: '', phone: '', role: 'signer', order: 1 }]);
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [providersRes, typesRes, methodsRes, processesRes, legalRes, firmawebRes] = await Promise.all([
        api.get('/firma-electronica/providers'),
        api.get('/firma-electronica/signature-types'),
        api.get('/firma-electronica/verification-methods'),
        api.get('/firma-electronica/my-processes'),
        api.get('/firma-electronica/legal-info'),
        api.get('/signature/info').catch(() => ({ data: null }))
      ]);
      
      setProviders(providersRes.data.providers || []);
      setSignatureTypes(typesRes.data.types || []);
      setVerificationMethods(methodsRes.data.methods || []);
      setMyProcesses(processesRes.data.processes || []);
      setLegalInfo(legalRes.data);
      setFirmawebInfo(firmawebRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('El archivo no debe superar 10MB');
        return;
      }
      setDocumentFile(file);
      if (!documentName) {
        setDocumentName(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };
  
  const addSigner = () => {
    setSigners([...signers, { 
      rut: '', 
      name: '', 
      email: '', 
      phone: '', 
      role: 'signer', 
      order: signers.length + 1 
    }]);
  };
  
  const removeSigner = (index) => {
    if (signers.length > 1) {
      setSigners(signers.filter((_, i) => i !== index));
    }
  };
  
  const updateSigner = (index, field, value) => {
    const updated = [...signers];
    updated[index][field] = value;
    setSigners(updated);
  };
  
  const handleCreateProcess = async () => {
    // Validation
    if (!documentName) {
      toast.error('Ingresa el nombre del documento');
      return;
    }
    if (!documentFile) {
      toast.error('Selecciona un archivo');
      return;
    }
    if (signers.some(s => !s.rut || !s.name || !s.email)) {
      toast.error('Completa todos los datos de los firmantes');
      return;
    }
    
    setCreating(true);
    try {
      // Convert file to base64
      const base64 = await fileToBase64(documentFile);
      
      const response = await api.post('/firma-electronica/create-process', {
        document_id: `doc-${Date.now()}`,
        document_name: documentName,
        document_content_base64: base64,
        signers: signers,
        signature_type: selectedType,
        verification_method: selectedMethod,
        provider: selectedProvider,
        expiry_hours: 72
      });
      
      toast.success('Proceso de firma creado exitosamente');
      setActiveTab('processes');
      loadInitialData();
      
      // Reset form
      setDocumentName('');
      setDocumentFile(null);
      setSigners([{ rut: '', name: '', email: '', phone: '', role: 'signer', order: 1 }]);
    } catch (error) {
      console.error('Error creating process:', error);
      toast.error(error.response?.data?.detail || 'Error al crear proceso');
    } finally {
      setCreating(false);
    }
  };
  
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };
  
  const getTypeIcon = (typeId) => {
    switch (typeId) {
      case 'simple': return Shield;
      case 'advanced': return Lock;
      case 'biometric': return Fingerprint;
      default: return PenTool;
    }
  };
  
  const getMethodIcon = (methodId) => {
    switch (methodId) {
      case 'otp_email': return Mail;
      case 'otp_sms': return Smartphone;
      case 'clave_unica': return KeyRound;
      case 'biometric_facial': return User;
      case 'biometric_fingerprint': return Fingerprint;
      default: return Shield;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="electronic-signature-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <PenTool className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Firma Electrónica Avanzada</h1>
                <p className="text-xs text-slate-400">FEA - Ley 19.799 Chile</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'create'
                ? 'bg-violet-500/20 text-violet-400 border-b-2 border-violet-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Plus className="w-4 h-4" />
            Nuevo Proceso
          </button>
          <button
            onClick={() => setActiveTab('processes')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'processes'
                ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4" />
            Mis Procesos
            {myProcesses.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                {myProcesses.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('firmaweb')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'firmaweb'
                ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            FirmaWeb
            <span className="px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded text-[10px] font-bold">PRO</span>
          </button>
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'info'
                ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Info className="w-4 h-4" />
            Información Legal
          </button>
        </div>
        
        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Form */}
            <div className="space-y-6">
              {/* Document Info */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-400" />
                  Documento a Firmar
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Nombre del documento</label>
                    <input
                      type="text"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      placeholder="Ej: Contrato de arriendo"
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Archivo (PDF, DOC, DOCX)</label>
                    <div className="relative">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        id="document-file"
                      />
                      <label
                        htmlFor="document-file"
                        className="flex items-center justify-center w-full p-8 border-2 border-dashed border-slate-700 rounded-lg cursor-pointer hover:border-violet-500/50 transition-colors"
                      >
                        {documentFile ? (
                          <div className="text-center">
                            <FileText className="w-10 h-10 text-violet-400 mx-auto mb-2" />
                            <p className="text-white font-medium">{documentFile.name}</p>
                            <p className="text-sm text-slate-400">{(documentFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <FileText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                            <p className="text-slate-400">Haz clic para seleccionar archivo</p>
                            <p className="text-xs text-slate-500">Máx. 10MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Signature Settings */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  Configuración de Firma
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Tipo de Firma</label>
                    <div className="grid grid-cols-3 gap-2">
                      {signatureTypes.map(type => {
                        const Icon = getTypeIcon(type.id);
                        return (
                          <button
                            key={type.id}
                            onClick={() => setSelectedType(type.id)}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              selectedType === type.id
                                ? 'bg-violet-500/20 border-violet-500 text-violet-400'
                                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                            }`}
                          >
                            <Icon className="w-5 h-5 mx-auto mb-1" />
                            <p className="text-xs font-medium">{type.id === 'simple' ? 'Simple' : type.id === 'advanced' ? 'Avanzada' : 'Biométrica'}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Método de Verificación</label>
                    <select
                      value={selectedMethod}
                      onChange={(e) => setSelectedMethod(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                    >
                      {verificationMethods.map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Proveedor</label>
                    <select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                    >
                      {providers.map(provider => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name} {provider.supports_fea ? '(FEA)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Signers */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-400" />
                    Firmantes
                  </h3>
                  <button
                    onClick={addSigner}
                    className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar
                  </button>
                </div>
                
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {signers.map((signer, index) => (
                    <div key={index} className="p-4 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-400">Firmante {index + 1}</span>
                        {signers.length > 1 && (
                          <button
                            onClick={() => removeSigner(index)}
                            className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <RutField
                          testid={`esign-signer-rut-${index}`}
                          value={signer.rut}
                          onChange={(v) => updateSigner(index, 'rut', v)}
                          onLookup={(d) => setSigners((prev) => prev.map((s, i) => (i === index ? fillEmpty(s, { name: d.name }) : s)))}
                          placeholder="RUT"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          value={signer.name}
                          onChange={(e) => updateSigner(index, 'name', e.target.value)}
                          placeholder="Nombre completo"
                          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none"
                        />
                        <input
                          type="email"
                          value={signer.email}
                          onChange={(e) => updateSigner(index, 'email', e.target.value)}
                          placeholder="Email"
                          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none"
                        />
                        <input
                          type="tel"
                          value={signer.phone}
                          onChange={(e) => updateSigner(index, 'phone', e.target.value)}
                          placeholder="Teléfono (opcional)"
                          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                onClick={handleCreateProcess}
                disabled={creating}
                className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creando proceso...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Iniciar Proceso de Firma
                  </>
                )}
              </button>
              
              {/* Info */}
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4">
                <p className="text-sm text-slate-400">
                  <span className="text-violet-400 font-medium">Firma Electrónica Avanzada:</span> Los firmantes recibirán un email con el enlace para firmar. La firma tiene validez legal equivalente a firma manuscrita según la Ley 19.799.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Processes Tab */}
        {activeTab === 'processes' && (
          <div className="space-y-4">
            {myProcesses.length > 0 ? (
              myProcesses.map(process => {
                const statusConfig = STATUS_CONFIG[process.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div key={process.id} className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-white">{process.document_name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${statusConfig.color}-500/20 text-${statusConfig.color}-400 flex items-center gap-1`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-400 mb-3">
                          <span>{process.signature_type === 'advanced' ? 'FEA' : 'FES'}</span>
                          <span>•</span>
                          <span>{process.signers?.length || 0} firmantes</span>
                          <span>•</span>
                          <span>{new Date(process.created_at).toLocaleDateString('es-CL')}</span>
                        </div>
                        
                        {/* Signers Progress */}
                        <div className="flex flex-wrap gap-2">
                          {process.signers?.map((signer, index) => (
                            <span
                              key={index}
                              className={`px-2 py-1 rounded text-xs ${
                                signer.signed
                                  ? 'bg-green-500/20 text-green-400'
                                  : signer.rejected
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-slate-700 text-slate-400'
                              }`}
                            >
                              {signer.name} {signer.signed ? '✓' : signer.rejected ? '✗' : '○'}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {process.status === 'signed' && (
                          <button className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors" title="Descargar certificado">
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <button className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors" title="Ver detalles">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <PenTool className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-400 mb-2">Sin procesos de firma</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Crea tu primer proceso de firma electrónica
                </p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
                >
                  Crear proceso
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* FirmaWeb Tab */}
        {activeTab === 'firmaweb' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Sparkles className="w-7 h-7 text-purple-400" />
                    FirmaWeb
                    <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded text-xs font-bold">INTEGRACIÓN PRO</span>
                  </h3>
                  <p className="text-slate-400 mt-2">
                    Firma Electrónica con validez legal completa - Ley 19.799 Chile
                  </p>
                </div>
                {firmawebInfo?.is_configured ? (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Conectado
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-full text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Modo Demo
                  </span>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* FES - Firma Simple */}
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Mail className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Firma Electrónica Simple (FES)</h4>
                    <p className="text-xs text-slate-400">Mediante código OTP</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm text-slate-400">
                    Firma mediante código de verificación enviado por email o SMS. Ideal para:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      Contratos privados
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      Acuerdos comerciales
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      Documentos internos
                    </li>
                  </ul>
                  <div className="pt-3 border-t border-slate-700">
                    <p className="text-xs text-slate-500">Autenticación: OTP Email, OTP SMS</p>
                  </div>
                </div>
              </div>

              {/* FEA - Firma Avanzada */}
              <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-500/20 rounded-xl">
                    <Shield className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      Firma Electrónica Avanzada (FEA)
                      <span className="px-1.5 py-0.5 bg-purple-500 text-white rounded text-[10px]">MÁXIMA VALIDEZ</span>
                    </h4>
                    <p className="text-xs text-slate-400">Con certificado digital</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm text-slate-400">
                    Firma con certificado digital para máxima validez legal. Ideal para:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                      Escrituras públicas
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                      Documentos notariales
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-purple-400" />
                      Trámites oficiales
                    </li>
                  </ul>
                  <div className="pt-3 border-t border-purple-500/30">
                    <p className="text-xs text-purple-300">Autenticación: Certificado Digital, ClaveÚnica</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Características de la Integración
              </h4>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {firmawebInfo?.features && Object.entries(firmawebInfo.features).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-slate-300">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
              <h4 className="font-semibold text-emerald-400 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Cumplimiento Legal
              </h4>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                  <p className="text-2xl font-bold text-white">Ley 19.799</p>
                  <p className="text-xs text-slate-400 mt-1">Marco legal Chile</p>
                </div>
                <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                  <p className="text-lg font-bold text-emerald-400">Plena Validez</p>
                  <p className="text-xs text-slate-400 mt-1">Equivalente a firma manuscrita</p>
                </div>
                <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                  <p className="text-lg font-bold text-purple-400">Proveedor Acreditado</p>
                  <p className="text-xs text-slate-400 mt-1">Certificación oficial</p>
                </div>
              </div>
            </div>

            {/* Setup Instructions */}
            {!firmawebInfo?.is_configured && firmawebInfo?.setup_instructions && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                  <KeyRound className="w-5 h-5" />
                  Configuración para Producción
                </h4>
                <pre className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-900/50 p-4 rounded-lg">
                  {firmawebInfo.setup_instructions}
                </pre>
                <a 
                  href="https://www.firmaweb.app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-sm font-medium"
                >
                  Ir a FirmaWeb
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        )}
        
        {/* Info Tab */}
        {activeTab === 'info' && legalInfo && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  Marco Legal
                </h3>
                <div className="space-y-3">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="font-medium text-white">{legalInfo.legal_framework?.law}</p>
                    <p className="text-sm text-slate-400">{legalInfo.legal_framework?.name}</p>
                    <p className="text-xs text-slate-500 mt-1">Fecha: {legalInfo.legal_framework?.date}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-slate-400">Regulador:</p>
                    <p className="text-white">{legalInfo.legal_framework?.regulator}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">Tipos de Firma</h3>
                <div className="space-y-3">
                  {Object.entries(legalInfo.signature_types || {}).map(([key, type]) => (
                    <div key={key} className={`p-4 rounded-lg ${key === 'FEA' ? 'bg-violet-500/10 border border-violet-500/30' : 'bg-slate-800/50'}`}>
                      <p className={`font-medium ${key === 'FEA' ? 'text-violet-400' : 'text-white'}`}>{type.name}</p>
                      <p className="text-sm text-slate-400 mt-1">{type.validity}</p>
                      <p className="text-xs text-slate-500 mt-1">{type.requirements}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">Proveedores Acreditados</h3>
                <div className="flex flex-wrap gap-2">
                  {legalInfo.accredited_providers?.map((provider, index) => (
                    <span key={index} className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm">
                      {provider}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4">Usos Comunes</h3>
                <ul className="space-y-2">
                  {legalInfo.common_uses?.map((use, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {use}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
                <h3 className="font-semibold text-emerald-400 mb-3">Beneficios</h3>
                <ul className="space-y-2">
                  {legalInfo.benefits?.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-slate-300">
                      <Sparkles className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ElectronicSignaturePage;
